
	import { defineComponent, PropType, Ref, ref } from 'vue';

	import { ProviderService } from '@/Services/provider-service';
	import { TreatmentService } from '@/Services/treatment-service';
	import { DateHelper } from '@/Services/Helper/date-helper';
	import { ValidationHelper } from '@/Services/Helper/validation-helper';

	import { LookupKeys } from '@/Enums/LookupKeys';

	import { IProvider } from '@/Models/IProvider';
	import { ITreatment, ITreatmentForEdit } from '@/Models/ITreatment';
	import { IBaseLookup } from '@/Models/Lookup/BaseLookup';
	import { MasterLookupWrapper } from '@/Models/Lookup/MasterLookupWrapper';

	import ValidationAlert from '@/ChildrenComponents/ValidationAlert.vue';
	import LoadingSpinner from '../LoadingSpinner.vue';

	export default defineComponent({
		name: "phase-treatment-dialog",
		components: { 
			LoadingSpinner,
			ValidationAlert 
		},
		emits: ["close", "saved"],
		props: {
			lookups: {
				type: Object as PropType<MasterLookupWrapper>,
				required: true
			},
			source: {
				type: Object as PropType<ITreatment>,
				required: true
			},
		},
		setup(): { form: Ref } {
			const form = ref(null);
			return { form };
		},
		created() {
			this.isLoading = true;
			this.setDto();
			this.statusList = this.lookups.lookupLists[LookupKeys.TreatmentStatus];
			this.typeList = this.lookups.lookupLists[LookupKeys.TreatmentType];
			this.setupProviders().then(() => {
				this.isLoading = false;
			});
		},
		data () {
			return {
				requiredFieldRules: ValidationHelper.requiredRules,
				dto: {} as ITreatmentForEdit,
				statusList: [] as IBaseLookup[],
				typeList: [] as IBaseLookup[],
				providerList: [] as IProvider[],
				errors: [] as string[],
				isSaving: false,
				isLoading: false,
			}
		},
		methods: {
			setDto(): void {
				this.dto = JSON.parse(JSON.stringify(this.source)) as ITreatmentForEdit;
				this.dto.startDateForEdit = DateHelper.formatDateUtc(this.dto.startDate, "yyyy-MM-dd");
				this.dto.endDateForEdit = DateHelper.formatDateUtc(this.dto.endDate, "yyyy-MM-dd");
				if (this.form != null) {
					//adding a brief delay to make sure the changes to startDateForEdit have fully registered before resetting validation
					setTimeout(() => { this.form.resetValidation(); }, 100);
				}
			},
			setupProviders(): Promise<void> {
				return ProviderService.getList().then(result => { 
					let selected_provider = undefined as undefined | IProvider;
					if (this.dto.providerId) {
						selected_provider = result.find(p => p.providerId === this.dto.providerId)
					}

					if (selected_provider !== undefined) {
						this.providerList = result.filter(p => p.isActive || p.providerId === selected_provider!.providerId)
					} else {
						this.providerList = result.filter(p => p.isActive);
					}
				});
			},
			prepForSave(): void {
				this.dto.startDate = DateHelper.standardizeDate(this.dto.startDateForEdit);
				this.dto.endDate = DateHelper.standardizeDate(this.dto.endDateForEdit);
                if (!this.dto.endDate) this.dto.endDate = this.dto.startDate;
				this.dto.statusName = this.statusList.find(x => x.id == this.dto.treatmentStatusId)?.description ?? "";
				this.dto.typeName = this.typeList.find(x => x.id == this.dto.treatmentTypeId)?.description ?? "";
				this.dto.providerName = this.dto.providerId == 1 ? this.dto.providerOther : this.providerList.find(x => x.providerId == this.dto.providerId)?.name ?? "";
			},
			closeDialog(): void {
				this.$emit("close");
			},
			async save(startNew: boolean) {
                if (!this.dto.endDateForEdit) this.dto.endDateForEdit = this.dto.startDateForEdit;
				const { valid } = await this.form.validate();
				if (valid) {
					this.prepForSave();
					this.isSaving = true;
					TreatmentService.save(this.dto)
						.then(result => {
							this.errors = result.messages;
							if (result.isValid && result.resultObject != null) {
								this.$emit("saved", result.resultObject, startNew);
							}
						})
						.catch(error => this.errors.push(error))
						.finally(() => this.isSaving = false);
				}
			},
		},
		computed: {
			buttonText1(): string {
				return this.isSaving ? "Saving..." : "Save and Close";
			},
			buttonText2(): string {
				return this.isSaving ? "Saving..." : "Save and Start New";
			},
			showProviderOther(): boolean {
				return this.dto.providerId == 1;
			},
			endDateRules() {
				return [
					(v : string) => {
						if (this.dto) {
							const startDate = new Date(this.dto.startDateForEdit);
							const endDate = new Date(v);
							return !startDate || endDate >= startDate || 'End Date cannot be before Start Date';
						}
						return true; // Return true if this.dto is null
					}
				];
			},
		},
		watch: {
			source() {
				this.isLoading = true;
				this.setDto();
				this.setupProviders().then(() => {
					this.isLoading = false;
				});
			},
			// 'dto.startDateForEdit': {
			// 	handler(value) {
			// 		if (this.dto && !this.dto?.endDateForEdit) {
			// 			this.dto.endDateForEdit = value;
			// 		}
			// 	},
			// 	deep: true
			// },
		},
	});
